import { createContext, useContext, useState } from "react";
import { UserProfileType, WalletType } from "../types/user";

interface AppContextType {
  loading: boolean;
  loadingText: string;
  startLoading: (s?: string) => void;
  stopLoading: () => void;
  isSidebarOpen: boolean;
  showSidebar: () => void;
  hideSidebar: () => void;
  walletList: WalletType[];
  addWalletToList: (w: WalletType) => void;
  updateWalletList: (w: WalletType[]) => void;
  user?: UserProfileType;
  setUser: (u: UserProfileType | undefined) => void;
  role: string;
  setRole: (r: string) => void;
  keycloakProfile?: UserProfileType;
  setProfile: (p: UserProfileType) => void;
  userProfilePic: any;
  setProfilePic: (p: any) => void;
  isMessagesModalOpen: boolean;
  showMessagesModal: () => void;
  hideMessagesModal: () => void;
}

const AppContext = createContext<AppContextType>({
  loading: true,
  loadingText: "Loading",
  startLoading: () => {},
  stopLoading: () => {},
  isSidebarOpen: false,
  showSidebar: () => {},
  hideSidebar: () => {},
  walletList: [],
  addWalletToList: () => {},
  updateWalletList: () => {},
  user: undefined,
  setUser: () => {},
  role: "",
  setRole: () => {},
  keycloakProfile: undefined,
  setProfile: () => {},
  userProfilePic: {},
  setProfilePic: () => {},
  isMessagesModalOpen: false,
  showMessagesModal: () => {},
  hideMessagesModal: () => {},
});

export function AppContextProvider({ children }: any) {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>("Loading");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [walletList, setWalletList] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfileType | undefined>();
  const [userRole, setUserRole] = useState<string>("");
  const [keycloakProfile, setKeycloakProfile] = useState<UserProfileType>();
  const [userProfilePic, setUserProfilePic] = useState<object>();
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);

  const startLoading = (s?: string) => {
    setLoadingText(s || "Loading");
    setLoading(true);
  };

  const stopLoading = () => {
    setLoadingText("");
    setLoading(false);
  };

  const showSidebar = () => {
    setIsSidebarOpen(true);
  };

  const hideSidebar = () => {
    setIsSidebarOpen(false);
  };

  const addWalletToList = (w: WalletType) => {
    setWalletList((existingW) => [...existingW, w]);
  };

  const updateWalletList = (w: WalletType[]) => {
    setWalletList(w);
  };

  const setUser = (u?: UserProfileType) => {
    setUserProfile(u);
  };

  const setRole = (r: string) => {
    setUserRole(r);
  };

  const setProfile = (p: UserProfileType) => {
    setKeycloakProfile(p);
  };

  const setProfilePic = (p: object) => {
    setUserProfilePic(p);
  };

  const showMessagesModal = () => {
    setIsMessagesModalOpen(true);
  };

  const hideMessagesModal = () => {
    setIsMessagesModalOpen(false);
  };

  const value = {
    loading,
    loadingText,
    startLoading,
    stopLoading,
    isSidebarOpen,
    showSidebar,
    hideSidebar,
    walletList,
    addWalletToList,
    updateWalletList,
    user: userProfile,
    setUser,
    role: userRole,
    setRole,
    keycloakProfile,
    setProfile,
    userProfilePic,
    setProfilePic,
    isMessagesModalOpen,
    showMessagesModal,
    hideMessagesModal,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);
  return context;
}
